import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { apiClient } from '@/api/apiClient';
import { apiError } from '@/api/admin';
import { login } from '@/api/auth';
import { useAuthStore } from '@/auth/authStore';
import { hashPin, randomBytes, toBase64, fromBase64 } from '@/offline/crypto';
import { useLockStore } from '@/offline/lockStore';

const PIN_KEY = 'annova.lock.pin';

/** Remembers a PIN's hash (salted PBKDF2) after an online unlock, so the screen can be unlocked offline with the same PIN. */
async function rememberPin(pin: string) {
  const salt = randomBytes(16);
  try { localStorage.setItem(PIN_KEY, JSON.stringify({ salt: toBase64(salt), hash: await hashPin(pin, salt) })); } catch { /* storage unavailable */ }
}

async function checkRememberedPin(pin: string): Promise<boolean> {
  try {
    const raw = localStorage.getItem(PIN_KEY);
    if (!raw) return false;
    const { salt, hash } = JSON.parse(raw) as { salt: string; hash: string };
    return (await hashPin(pin, fromBase64(salt))) === hash;
  } catch { return false; }
}

/**
 * The lock after idling (ToR 4.3.2): the session stays, the screen does not. Unlocked online by the
 * sign-in PIN (attested by the server) or by the password; offline by the PIN against the hash kept
 * at the last online unlock, which only re-opens what is in memory — nothing new is fetched.
 */
export default function LockScreen() {
  const locked = useLockStore((s) => s.locked);
  const unlock = useLockStore((s) => s.unlock);
  const user = useAuthStore((s) => s.user);
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'pin' | 'password'>('pin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (!locked) { setPin(''); setPassword(''); setError(null); } }, [locked]);
  if (!locked || !user) return null;

  const byPin = async () => {
    setBusy(true); setError(null);
    try {
      if (navigator.onLine) {
        await apiClient.post('/api/v1/auth/pin/attest', { pin, purpose: 'unlock', subjectType: 'Session', subjectId: null });
        await rememberPin(pin);
        unlock();
      } else if (await checkRememberedPin(pin)) unlock();
      else setError('That PIN does not match the one used at the last online unlock; try the password when the network returns.');
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const byPassword = async () => {
    setBusy(true); setError(null);
    try {
      if (!navigator.onLine) { setError('The password needs a network; offline, unlock with the PIN.'); return; }
      const result = await login(user.email, password);
      if ('mfaToken' in result) { setError('Your account needs a second factor: sign in again from the sign-in page.'); return; }
      unlock();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const signOut = () => { useAuthStore.getState().clear(); window.location.assign('/login?reason=idle'); };

  return (
    <Dialog open fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
      <Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Stack gap={2} sx={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 48, alignSelf: 'center', color: 'primary.main' }} />
          <Typography variant="h5">Locked</Typography>
          <Typography color="text.secondary">You were away for a while. Unlock to carry on as {user.fullName ?? user.email}.{navigator.onLine ? '' : ' You are offline: the sign-in PIN unlocks.'}</Typography>
          {mode === 'pin' ? (
            <>
              <TextField label="Sign-in PIN" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))} autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && pin.length >= 4) void byPin(); }} />
              <Button variant="contained" onClick={byPin} disabled={busy || pin.length < 4}>Unlock</Button>
              {navigator.onLine && <Button size="small" color="inherit" onClick={() => setMode('password')}>Use the password instead</Button>}
            </>
          ) : (
            <>
              <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && password) void byPassword(); }} />
              <Button variant="contained" onClick={byPassword} disabled={busy || !password}>Unlock</Button>
              <Button size="small" color="inherit" onClick={() => setMode('pin')}>Use the PIN instead</Button>
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Button size="small" color="inherit" onClick={signOut}>Sign out</Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
